import {
  Box,
  Button,
  Modal,
  TextField,
  Typography,
  IconButton,
  Select,
  MenuItem,
  SelectChangeEvent,
  Alert,
  CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useState } from 'react';
import { raiseDispute } from '../../services/BookingService';
import { CheckCircleOutline, ErrorOutline } from '@mui/icons-material';

export type BookingDisputeProps = {
  open: boolean;
  onClose: () => void;
  bookingId: number;
  professionalId: string;
};

const disputeOptions = [
  {
    label: 'They did not have the correct expertise',
    value: 1,
  },
  {
    label: 'Professional did not show up',
    value: 2,
  },
  {
    label: 'Received false information',
    value: 3,
  },
];

export const BookingDispute = ({
  open,
  onClose,
  bookingId,
  professionalId,
}: BookingDisputeProps) => {
  const [disputeReason, setDisputeReason] = useState<number | undefined>(
    undefined
  );
  const [explanation, setExplanation] = useState<string | undefined>(undefined);
  const [loading, setLoading] = useState<boolean>(false);
  const [selectedReasonError, setSelectedReasonError] = useState<
    string | undefined
  >();
  const [explanationError, setExplanationError] = useState<
    string | undefined
  >();
  const [status, setStatus] = useState<'success' | 'error' | undefined>(
    undefined
  );

  const getDisputeOutcome = () => {
    if (status === undefined) {
      return null;
    }
    if (status === 'success') {
      return (
        <Alert
          icon={<CheckCircleOutline fontSize="inherit" />}
          severity={status}
          sx={{ mt: 2 }}
        >
          Your dispite has been submitted successfully. We will review it and
          get back to you shortly.
        </Alert>
      );
    } else {
      return (
        <Alert
          icon={<ErrorOutline fontSize="inherit" />}
          severity={status}
          sx={{ mt: 2 }}
        >
          Your dispute could not be submitted. Please try again later.
        </Alert>
      );
    }
  };

  const handleReasonChange = (event: SelectChangeEvent<number>) => {
    setDisputeReason(Number(event.target.value));
  };

  const handleSubmit = async () => {
    setStatus(undefined);

    if (!disputeReason) {
      setSelectedReasonError('Please select a reason for the dispute');
    }
    if (!explanation) {
      setExplanationError('Please provide an explanation for the dispute');
    }
    if (!disputeReason || !explanation) {
      return;
    }

    const token = localStorage.getItem('token');
    try {
      setLoading(true);
      await raiseDispute(
        {
          bookingId,
          professionalId,
          disputeReason,
          explanation,
        },
        token ?? ''
      );
      setStatus('success');
    } catch (error) {
      console.error('Error raising dispute:', error);
      setStatus('error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal open={open}>
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 400,
          bgcolor: 'background.paper',
          borderRadius: 1,
          boxShadow: 24,
          p: 4,
        }}
      >
        <IconButton
          onClick={onClose}
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
          }}
        >
          <CloseIcon />
        </IconButton>

        <Typography
          variant="h6"
          textAlign="center"
          gutterBottom
          fontWeight={600}
        >
          Create Dispute
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          textAlign="left"
          sx={{ mt: 2 }}
          fontWeight={500}
        >
          If you are unhappy with the advice you got during your call, you can
          log a dispute with the CircleBe team here.
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          textAlign="left"
          sx={{ mt: 2 }}
          fontWeight={500}
          textTransform={'uppercase'}
        >
          DISPUTE REASON
        </Typography>
        <Select
          error={selectedReasonError !== undefined}
          sx={{ mt: 1 }}
          id="dispute-reason"
          value={disputeReason}
          onChange={handleReasonChange}
          fullWidth
          onFocus={() => setSelectedReasonError(undefined)}
          variant="outlined"
        >
          {disputeOptions.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
        <Typography color="error" align={'left'}>
          {selectedReasonError}
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          textAlign="left"
          sx={{ mt: 2 }}
          fontWeight={600}
          textTransform={'uppercase'}
        >
          EXPLANATION
        </Typography>
        <TextField
          error={explanationError !== undefined}
          autoFocus
          margin="dense"
          id="explanation"
          type="text"
          fullWidth
          variant="outlined"
          label="Tell us what happened..."
          multiline
          rows={4}
          onChange={(e) => setExplanation(e.target.value)}
          onFocus={() => setExplanationError(undefined)}
        />
        <Typography color="error" align={'left'}>
          {explanationError}
        </Typography>
        {getDisputeOutcome()}
        <Button
          fullWidth
          onClick={handleSubmit}
          variant="contained"
          sx={{ mt: 2, borderRadius: 2 }}
          color="secondary"
          disabled={loading || status === 'success'}
        >
          {loading ? <CircularProgress size={24} color="inherit" /> : 'Submit'}
        </Button>
      </Box>
    </Modal>
  );
};
